import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createDealerContact, deleteDealerContact, fetchDealerContacts, updateDealerContact } from '../services/dealerContacts';
import { dealerKeys } from './useDealersQuery';
import type { CreateDealerContactPayload, UpdateDealerContactPayload } from '../types/dealers.types';

export const useDealerContacts = (dealerId: string) => {
    return useQuery({
        queryKey: dealerKeys.contacts(dealerId),
        queryFn: () => fetchDealerContacts(dealerId),
        enabled: !!dealerId,
    });
};

export const useDealerContactMutations = (dealerId: string) => {
    const queryClient = useQueryClient();

    const invalidate = async () => {
        await queryClient.invalidateQueries({ queryKey: dealerKeys.contacts(dealerId) });
    };

    const createMutation = useMutation({
        mutationFn: (payload: CreateDealerContactPayload) => createDealerContact(dealerId, payload),
        onSuccess: invalidate,
    });

    const updateMutation = useMutation({
        mutationFn: ({ contactId, payload }: { contactId: string; payload: UpdateDealerContactPayload }) =>
            updateDealerContact(dealerId, contactId, payload),
        onSuccess: invalidate,
    });

    const deleteMutation = useMutation({
        mutationFn: (contactId: string) => deleteDealerContact(dealerId, contactId),
        onSuccess: invalidate,
    });

    return {
        createMutation,
        updateMutation,
        deleteMutation,
    };
};
